import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import NewViewExperience from '@github-ui/saved-views/components/preview/NewViewExperience'
import {SingleSignOnBanner} from '@github-ui/single-sign-on-banner'
import {type ReactNode, useRef} from 'react'
import {graphql, useFragment} from 'react-relay'

import {useActiveSearchQuery, useQueryEditState} from '../../contexts/QueryContext'
import {useSsoOrgDetection} from '../../hooks/use-sso-org-detection'
import type {LoadSearchQuery} from '../../pages/shared'
import type {IssueSearchType} from '../../types/issue-search-type'
import type {SearchCurrentViewFragment$key} from './__generated__/SearchCurrentViewFragment.graphql'
import type {SearchRepositoryFragment$key} from './__generated__/SearchRepositoryFragment.graphql'
import type {SearchRootFragment$key} from './__generated__/SearchRootFragment.graphql'
import styles from './Search.module.css'
import {SearchBar} from './SearchBar'
import {SearchList} from './SearchList'

type SearchProps = {
  itemIdentifier: ItemIdentifier | undefined
  currentViewKey: SearchCurrentViewFragment$key
  currentRepository: SearchRepositoryFragment$key | null
  search: SearchRootFragment$key
  loadSearchQuery?: LoadSearchQuery
  showSsoBanner?: boolean
  queryFromCustomView?: string | null
  onSidePanelNavigate?: (issue: ItemIdentifier) => void
  trackIssueNavigation: (position?: number) => void
  updateIssueSearchType?: (issueSearchType: string | null | undefined) => void
  onListItemsChange?: (items: ItemIdentifier[]) => void
  searchType?: IssueSearchType
  /**
   * Render-prop escape hatch for placing the filter row and results region
   * into separate landmark slots (e.g. `<FilteredListPageLayout.FilterBar>`
   * and `<FilteredListPageLayout.Results>`). When provided, Search does NOT
   * render its default `<div>` container — the consumer owns layout. When
   * omitted, behavior is unchanged.
   *
   * Typically supplied via the matching prop on `List` rather than wired
   * directly here — `List` adds the page-level `header` slot on top.
   */
  renderSlotted?: (parts: {filterBar: ReactNode; results: ReactNode}) => ReactNode
}

export function Search({
  itemIdentifier,
  currentViewKey,
  currentRepository,
  search,
  loadSearchQuery,
  queryFromCustomView,
  onSidePanelNavigate,
  showSsoBanner,
  trackIssueNavigation,
  updateIssueSearchType,
  onListItemsChange,
  searchType,
  renderSlotted,
}: SearchProps) {
  const activeSearchQuery = useActiveSearchQuery()
  const {isNewView} = useQueryEditState()
  const {orgNames, allSsoOrgs, filterDetectedSsoOrgs, onSsoOrgsFromFilter} = useSsoOrgDetection(activeSearchQuery ?? '')

  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SearchRootFragment on Searchable
      @argumentDefinitions(
        query: {type: "String!"}
        first: {type: "Int"}
        labelPageSize: {type: "Int!"}
        skip: {type: "Int", defaultValue: null}
        fetchRepository: {type: "Boolean!"}
        type: {type: "SearchType", defaultValue: ISSUE_ADVANCED}
        showIssueFieldPills: {type: "Boolean!", defaultValue: false}
      ) {
        ...SearchList
          @arguments(
            query: $query
            first: $first
            labelPageSize: $labelPageSize
            fetchRepository: $fetchRepository
            skip: $skip
            type: $type
            showIssueFieldPills: $showIssueFieldPills
          )
      }
    `,
    search,
  )

  const repoData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SearchRepositoryFragment on Repository {
        ...SearchListRepo
        ...SearchBarRepo
      }
    `,
    currentRepository,
  )

  const currentViewData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment SearchCurrentViewFragment on Shortcutable {
        ...SearchBarCurrentViewFragment
      }
    `,
    currentViewKey,
  )

  const listRef = useRef<HTMLUListElement | undefined>(undefined)

  const filterBar = (
    <SearchBar
      currentViewKey={currentViewData}
      currentRepository={repoData ?? null}
      queryFromCustomView={queryFromCustomView}
      onSsoOrgsFromFilter={onSsoOrgsFromFilter}
      ssoOrgNames={orgNames}
    />
  )

  const results =
    isNewView && !activeSearchQuery ? (
      <NewViewExperience />
    ) : (
      <>
        <SingleSignOnBanner protectedOrgs={showSsoBanner ? allSsoOrgs : filterDetectedSsoOrgs} />
        <SearchList
          itemIdentifier={itemIdentifier}
          search={data}
          repository={repoData ?? null}
          loadSearchQuery={loadSearchQuery}
          query={activeSearchQuery}
          queryFromCustomView={queryFromCustomView}
          listRef={listRef}
          onSidePanelNavigate={onSidePanelNavigate}
          onIssueNavigate={trackIssueNavigation}
          updateIssueSearchType={updateIssueSearchType}
          onListItemsChange={onListItemsChange}
          searchType={searchType}
          hasSsoOrgs={filterDetectedSsoOrgs.length > 0}
        />
      </>
    )

  if (renderSlotted) {
    return <>{renderSlotted({filterBar, results})}</>
  }

  return (
    <div className={styles.SearchContainer}>
      {filterBar}
      {results}
    </div>
  )
}
